import UpperCaseText from "./UpperCaseText";
import {
  PURCHASE_TYPES,
  PROD_SCHEDULER_URL,
  QA_SCHEDULER_URL,
} from "../common/Constants";

const CANCELLED_STATUSES = [
  "DEALER_CANCELLED",
  "ORDER_CANCELLED",
  "RESERVATION_CANCELLED",
];

const ACCEPT_ELIGIBLE_PURCHASE_TYPES = [
  PURCHASE_TYPES.THIRD_PARTY, //Pre arranged finance
  PURCHASE_TYPES.CREDIT_APPLICATION, //Credit app approved
  PURCHASE_TYPES.SIGN_ONLINE, //eSign
  PURCHASE_TYPES.DISCUSS_WITH_DEALER, //Discuss at Dealer
  PURCHASE_TYPES.SIGN_OFFLINE, //Sign offline
  PURCHASE_TYPES.CASH, //Cash
  PURCHASE_TYPES.CONTINUE_AT_DEALER, //Continue at Dealer
  PURCHASE_TYPES.SIGN_AT_DEALER,
  PURCHASE_TYPES.ABANDONED_CHECKOUT,
];

const ORA_STATUSES = ["NO_DEALER_ACTION", "ORDER_CONFIRMED", "AMENDED"];

const RESERVATIONS_SUBMITTED_STATUSES = [
  "CREATED",
  "RESERVATION_CANCELLED",
  "AMENDED",
  "DEALER_CANCELLED",
  "ORDER_CANCELLED",
];

const ORDERS_SUBMITTED_STATUSES = [
  "DEALER_REJECTED",
  "RESERVATION_CANCELLED",
  "ORDER_CANCELLED",
  "ORDERED",
  "DEALER_ACCEPTED",
  "CONTRACT_REQUESTED",
  "CONTRACT_REQUEST_FAILED",
  "CONTRACT_FAILED",
  "ORDER_CONTRACTED",
  "CONTRACT_CANCEL_REQUESTED",
  "CONTRACT_CANCEL_FAILED",
  "CONVERTED_TO_OFFLINE",
  "CONTRACT_CANCEL_REQUEST_FAILED",
  "DEALER_CANCELLED",
  "CREATED",
];

const IS_CANADA_MARKET = (market) => {
  return UpperCaseText(market) === "CAN";
};

const IS_USA_MARKET = (market) => {
  return UpperCaseText(market) === "USA";
};

const IS_NA_MARKET = (market) => {
  return UpperCaseText(market) === "USA" || UpperCaseText(market) === "CAN";
};

const IS_CRC_USER = (user) => {
  return UpperCaseText(user) === "CRC";
};

const IS_FORD_BRAND = (brand) => {
  return "F" === brand;
};

const IS_MODEL_E_ORDER = (journeyType) => {
  return journeyType?.toUpperCase() === "MODEL-E";
};

const HAS_VALUE = (input) => {
  return input !== null && input !== undefined;
};

const NULL_OR_UNDEFINED = (input) => {
  return input === null || input === undefined;
};

const NOTNULL_OR_NOTUNDEFINED = (input) => {
  return !NULL_OR_UNDEFINED(input);
};

const FORMAT_COMMON_ID = (commonDealerId) => {
  return commonDealerId.replaceAll("|", "%7C");
};

const IS_FIELD_VIEW_USER = (user) => {
  return UpperCaseText(user) === "FIELD_VIEW";
};

const HAS_ONLY_EMP_ONBOARD_ROLE = (user) => {
  return user?.roles?.length === 1 && user.roles.includes("EMP_ONBOARD");
};

const HAS_EMP_ONBOARD_ROLE = (user) => {
  const roles = Array.isArray(user?.roles)
    ? user.roles
    : Object.values(user?.roles || {});

  return roles.some((role) => String(role).toUpperCase() === "EMP_ONBOARD");
};

const formatDate = (date) => {
  if (!date) {
    return;
  }

  // Handle different date formats for Safari compatibility
  let normalizedDate = date;
  let shouldUseUTC = false;

  if (typeof date === "string") {
    // Trim whitespace
    normalizedDate = date.trim();
    if (!normalizedDate) {
      return;
    }

    // Check if it's an ISO format date (contains T or Z)
    if (normalizedDate.includes("T") || normalizedDate.includes("Z")) {
      shouldUseUTC = true;
      // ISO format dates should work directly with new Date()
      normalizedDate = date;
    } else {
      // Convert MM-DD-YYYY to MM/DD/YYYY format for Safari compatibility
      normalizedDate = date.replaceAll("-", "/");
    }
  } else if (typeof date === "number") {
    // Handle numeric timestamps
    normalizedDate = date;
    shouldUseUTC = true;
  }

  const d = new Date(normalizedDate);

  // Check if date is valid
  if (Number.isNaN(d.getTime())) {
    console.warn(`Invalid date format: ${date}`);
    return;
  }

  // Use UTC methods for ISO dates, timestamps, and date objects to avoid timezone issues
  let month, day, year;
  if (shouldUseUTC || normalizedDate instanceof Date) {
    // For ISO dates, timestamps, and Date objects, use UTC methods to maintain consistency
    month = String(d.getUTCMonth() + 1).padStart(2, "0");
    day = String(d.getUTCDate()).padStart(2, "0");
    year = d.getUTCFullYear();
  } else {
    // For regular dates, use local methods
    month = String(d.getMonth() + 1).padStart(2, "0");
    day = String(d.getDate()).padStart(2, "0");
    year = d.getFullYear();
  }

  return `${month}/${day}/${year}`;
};

const upperCaseText = (text) => {
  return text ? text.toUpperCase() : "";
};

const capitalizeText = (text) => {
  if (!text) return "";

  const formattedText = text.toLowerCase().replaceAll("_", " ");
  return formattedText.charAt(0).toUpperCase() + formattedText.slice(1);
};

const formatPhoneNumber = (phoneNumber) => {
  if (phoneNumber === "") {
    return "";
  }

  // Remove all non-numeric characters
  const cleaned = `${phoneNumber}`.replace(/\D/g, "");

  // Extract area code, central office code, and station code
  const areaCode = cleaned.slice(0, 3);
  const centralOfficeCode = cleaned.slice(3, 6);
  const stationCode = cleaned.slice(6, 10);

  // Format the phone number
  return `(${areaCode}) ${centralOfficeCode}-${stationCode}`;
};

const formatToSimpleDate = (date) => {
  const dateStr = new Date(date);
  const month = String(dateStr.getMonth() + 1).padStart(2, "0");
  const day = String(dateStr.getDate()).padStart(2, "0");
  const year = dateStr.getFullYear();

  return `${month}/${day}/${year}`;
};

const GET_SCHEDULER_URL = (env) => {
  const schedulerUrls = {
    prod: PROD_SCHEDULER_URL,
    stg: QA_SCHEDULER_URL,
    edu: QA_SCHEDULER_URL,
    qa: QA_SCHEDULER_URL,
    dev: QA_SCHEDULER_URL,
    local: QA_SCHEDULER_URL,
  };

  return schedulerUrls[env];
};

const BASE_URL = globalThis.EMP?.BASE_URL ?? "";

const RESERVATION_BASE_URL = globalThis.RESERVATION_SERVICE?.BASE_URL ?? "";

const REPORTING_BASE_URL = globalThis.REPORTING_SERVICE?.BASE_URL ?? "";

const SHARED_SERVICE_URL = globalThis.EMP?.SHARED_SERVICE_URL ?? "";

const ENV = globalThis.EMP?.ENV ?? "";

const ACCESSORIES_PRICING_URL =
  globalThis.PRICING?.ACCESSORIES_PRICING_URL ?? "";

const AUTHORIZE_BASE_URL = globalThis.AUTH_SERVICE?.BASE_AUTHORIZE_URL ?? "";

const AUTHORIZE_HEALTH_URL = globalThis.AUTH_SERVICE?.HEALTH_URL ?? "";

const MESSAGES_BASE_URL = globalThis.MESSAGES_SERVICE?.BASE_URL ?? "";

const ONBOARDING_BASE_URL = globalThis.ONBOARDING_SERVICE?.BASE_URL ?? "";

const formatVehicleTrim = (str) => {
  if (str === null) {
    return;
  }

  if (str === "GT") {
    return "GT";
  }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const transformPurchaseType = (purchaseType) => {
  if (purchaseType === null || purchaseType === undefined) return "";

  return purchaseType.toUpperCase() === "SPOT BUY" ? "In Store" : purchaseType;
};

const stripBrandPrefix = (salesCode) => {
  if (!salesCode) return "";
  return salesCode.replace(/^[FL]/i, "");
};

const isDualDealer = (user) => {
  if (!user?.dealerGroupingsMap) {
    return false;
  }

  const keys = Object.keys(user.dealerGroupingsMap);
  const hasFordBrand = keys.some((key) => {
    return key.split("|").at(-1) === "F";
  });
  const hasLincolnBrand = keys.some((key) => {
    return key.split("|").at(-1) === "L";
  });

  return hasFordBrand && hasLincolnBrand;
};

export {
  CANCELLED_STATUSES,
  ORA_STATUSES,
  RESERVATIONS_SUBMITTED_STATUSES,
  ORDERS_SUBMITTED_STATUSES,
  IS_FORD_BRAND,
  IS_CANADA_MARKET,
  IS_USA_MARKET,
  IS_FIELD_VIEW_USER,
  HAS_ONLY_EMP_ONBOARD_ROLE,
  HAS_EMP_ONBOARD_ROLE,
  BASE_URL,
  SHARED_SERVICE_URL,
  RESERVATION_BASE_URL,
  REPORTING_BASE_URL,
  ACCESSORIES_PRICING_URL,
  AUTHORIZE_BASE_URL,
  AUTHORIZE_HEALTH_URL,
  MESSAGES_BASE_URL,
  ONBOARDING_BASE_URL,
  HAS_VALUE,
  ACCEPT_ELIGIBLE_PURCHASE_TYPES,
  NOTNULL_OR_NOTUNDEFINED,
  NULL_OR_UNDEFINED,
  ENV,
  FORMAT_COMMON_ID,
  IS_NA_MARKET,
  IS_CRC_USER,
  IS_MODEL_E_ORDER,
  GET_SCHEDULER_URL,
  formatDate,
  upperCaseText,
  capitalizeText,
  formatPhoneNumber,
  formatVehicleTrim,
  formatToSimpleDate,
  transformPurchaseType,
  stripBrandPrefix,
  isDualDealer,
};
